import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "firebase/auth";

import { authService } from "../lib/authService";
import { useSystemStore } from "@/stores/systemStore";

type AuthContextValue = {
  currentUser: User | null;
  authLoading: boolean;
  authError: Error | null;
  signIn: () => Promise<User>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    const subscribe = () => {
      if (!active) return;
      unsubscribe = authService.onAuthStateChanged((user) => {
        if (!active) return;
        useSystemStore.getState().start(user);
        setCurrentUser(user);
        setAuthLoading(false);
      });
    };
    if (import.meta.env.DEV) {
      void authService.bootstrapDevelopmentAuth().then(subscribe).catch((error: unknown) => {
        if (!active) return;
        setAuthError(error instanceof Error ? error : new Error(String(error)));
        setAuthLoading(false);
      });
    } else {
      subscribe();
    }

    return () => {
      active = false;
      unsubscribe?.();
      useSystemStore.getState().stop();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      currentUser,
      authLoading,
      authError,
      signIn: authService.signIn,
      signOut: authService.signOut,
    }),
    [currentUser, authLoading, authError],
  );

  return <AuthContext.Provider value={value}>{import.meta.env.DEV && authError
    ? <div role="alert">Development authentication failed: {authError.message}</div>
    : import.meta.env.DEV && authLoading
      ? <div role="status">Checking development authentication...</div>
      : children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
